/**
 * C059 — Calendario laboral (flujo REAL contra backend).
 *
 * Contratos reales (NexusOperationsController @Controller('work')):
 *   GET   /work/labor-calendar/:userId?period=AAAA-MM  → { userId, events[], impact }
 *   POST  /work/labor-calendar                         → evento PENDING
 *   PATCH /work/labor-calendar/:id/approve             → { approve: boolean, note? } (SOLO SUPERADMIN)
 *
 * Reglas preservadas: todo evento nace PENDING; SOLO lo APPROVED resta capacidad
 * (D3); CALENDAR_EXCEPTION sin reducción automática; unapproved sin efecto;
 * partial-day (allDay=false + hours) soportado por el contrato; sin inventar horas.
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../utils/api';
import { useAuthStore } from '../stores/authStore';
import { isSuperAdmin } from '../utils/permissions';
import { searchUsers } from '../utils/sharing';

const EVENT_TYPES = ['HOLIDAY', 'VACATION', 'PERMISSION', 'SICKNESS_ABSENCE', 'CALENDAR_EXCEPTION'] as const;

interface LaborEvent {
  id: string;
  userId: string;
  eventType: string;
  startDate: string;
  endDate: string;
  allDay: boolean;
  hours: number | null;
  reason: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  approvedBy: string | null;
}

interface D3Impact {
  period: string;
  minutes: number;
  daysAffected: string[];
  events: number;
}

const monthNow = () => new Date().toISOString().slice(0, 7);

export function LaborCalendarPanel({ userRole }: { userRole?: string } = {}) {
  const user = useAuthStore((s) => s.user);
  const canApprove = (userRole ? userRole === 'SUPERADMIN' : false) || isSuperAdmin(user?.role ?? '');

  const [period, setPeriod] = useState(monthNow());
  const [events, setEvents] = useState<LaborEvent[]>([]);
  const [impact, setImpact] = useState<D3Impact | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    eventType: 'VACATION',
    startDate: '',
    endDate: '',
    allDay: true,
    hours: '4',
    reason: '',
  });

  const userId = user?.id ?? '';
  // 089 — SUPERADMIN puede revisar el calendario de cualquier empleado del org.
  const [subjectId, setSubjectId] = useState('');
  const [subjectLabel, setSubjectLabel] = useState('');
  const [userQuery, setUserQuery] = useState('');
  const [userResults, setUserResults] = useState<Array<{ id: string; email: string; displayName?: string | null }>>([]);
  const targetUserId = subjectId || userId;

  const searchOrgUsers = async (q: string) => {
    setUserQuery(q);
    if (!canApprove || q.trim().length < 2) {
      setUserResults([]);
      return;
    }
    try {
      const found = await searchUsers(q.trim());
      setUserResults(Array.isArray(found) ? found : []);
    } catch {
      setUserResults([]);
    }
  };

  const load = useCallback(async () => {
    if (!targetUserId) return;
    setError(null);
    try {
      const { data } = await api.get(`/work/labor-calendar/${targetUserId}`, { params: { period } });
      setEvents(Array.isArray(data?.events) ? data.events : []);
      setImpact(data?.impact ?? null);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setError(err?.response?.data?.message || err?.message || 'No se pudo cargar el calendario laboral');
    }
  }, [targetUserId, period]);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const body: Record<string, unknown> = {
        eventType: form.eventType,
        startDate: form.startDate,
        endDate: form.endDate || form.startDate,
        allDay: form.allDay,
        reason: form.reason,
        ...(targetUserId && targetUserId !== userId ? { userId: targetUserId } : {}),
      };
      if (!form.allDay) body.hours = Number(form.hours);
      await api.post('/work/labor-calendar', body);
      setNotice('Evento creado con estado PENDING (pendiente de aprobación)');
      setForm((f) => ({ ...f, startDate: '', endDate: '', reason: '' }));
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setError(err?.response?.data?.message || err?.message || 'No se pudo crear el evento');
    } finally {
      setBusy(false);
    }
  };

  const decide = async (id: string, approve: boolean) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.patch(`/work/labor-calendar/${id}/approve`, { approve });
      setNotice(approve ? 'Evento APROBADO (el D3 descuenta su capacidad)' : 'Evento RECHAZADO (sin efecto en el D3)');
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setError(err?.response?.data?.message || err?.message || 'No se pudo resolver el evento');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="labor-calendar-panel" data-testid="labor-calendar-panel">
      <h2>Calendario laboral</h2>

      {canApprove && (
        <section data-testid="labor-subject-picker" className="mb-3">
          <label htmlFor="labor-user-search">
            Revisar calendario de un empleado (SUPERADMIN)
          </label>
          <input
            id="labor-user-search"
            type="search"
            data-testid="labor-user-search"
            placeholder="Buscar por email…"
            value={userQuery}
            onChange={(e) => void searchOrgUsers(e.target.value)}
            className="w-full max-w-sm"
          />
          {userResults.length > 0 && (
            <ul data-testid="labor-user-results" className="mt-1 space-y-1">
              {userResults.map((u) => (
                <li key={u.id}>
                  <button
                    type="button"
                    data-testid="labor-user-option"
                    onClick={() => {
                      setSubjectId(u.id);
                      setSubjectLabel(u.displayName || u.email);
                      setUserResults([]);
                      setUserQuery('');
                    }}
                  >
                    {u.displayName || u.email} · {u.email}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {subjectId && (
            <p data-testid="labor-subject-active" className="hint">
              Revisando el calendario de <strong>{subjectLabel || subjectId}</strong>{' '}
              <button
                type="button"
                data-testid="labor-subject-reset"
                onClick={() => { setSubjectId(''); setSubjectLabel(''); setUserQuery(''); }}
              >
                Volver a mi calendario
              </button>
            </p>
          )}
        </section>
      )}

      <div data-testid="labor-period">
        <label htmlFor="labor-period-input">Periodo (impacto D3)</label>
        <input
          id="labor-period-input" type="month" data-testid="labor-period-input"
          value={period} onChange={(e) => setPeriod(e.target.value)}
        />
      </div>

      {impact && (
        <div data-testid="labor-d3-impact" role="status">
          <h3>Impacto D3 · {impact.period}</h3>
          <p>Minutos de capacidad teórica descontados: <strong data-testid="labor-d3-minutes">{impact.minutes}</strong></p>
          <p>Días afectados: <strong>{impact.daysAffected.length}</strong> · Eventos aplicados: <strong>{impact.events}</strong></p>
          <p className="hint">Solo los eventos APPROVED descuentan capacidad. PENDING/REJECTED no tienen efecto.</p>
        </div>
      )}

      <section data-testid="labor-create-form">
        <h3>Nuevo evento</h3>
        <label>
          Tipo
          <select
            data-testid="labor-event-type"
            value={form.eventType}
            onChange={(e) => setForm((f) => ({ ...f, eventType: e.target.value }))}
          >
            {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label>
          Desde
          <input
            type="date" data-testid="labor-start-date" value={form.startDate}
            onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
          />
        </label>
        <label>
          Hasta
          <input
            type="date" data-testid="labor-end-date" value={form.endDate}
            onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
          />
        </label>
        <label>
          <input
            type="checkbox" data-testid="labor-all-day" checked={form.allDay}
            onChange={(e) => setForm((f) => ({ ...f, allDay: e.target.checked }))}
          />
          Día completo
        </label>
        {!form.allDay && (
          <label>
            Horas del evento parcial
            <input
              type="number" min={1} max={24} step={1} data-testid="labor-hours"
              value={form.hours} onChange={(e) => setForm((f) => ({ ...f, hours: e.target.value }))}
            />
          </label>
        )}
        <label>
          Motivo
          <input
            type="text" data-testid="labor-reason" value={form.reason}
            onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
            placeholder="Motivo del evento (mín. 3 caracteres)"
          />
        </label>
        <button
          data-testid="labor-create-btn" onClick={() => void create()} disabled={busy || !form.startDate || form.reason.trim().length < 3}
        >
          Registrar evento
        </button>
        <p className="hint">Todo evento se registra PENDING; solo SUPERADMIN puede aprobar o rechazar.</p>
      </section>

      <section data-testid="labor-events-list">
        <h3>Eventos</h3>
        {events.length === 0 && <p data-testid="labor-empty">No hay eventos registrados en este periodo.</p>}
        <ul>
          {events.map((ev) => (
            <li
              key={ev.id}
              data-testid="labor-event-row"
              data-status={ev.status}
              className="flex flex-wrap items-center gap-x-2 gap-y-1"
            >
              <span>{ev.eventType}</span>{' '}
              <span>{ev.startDate} → {ev.endDate}</span>{' '}
              <span>{ev.allDay ? 'día completo' : `${ev.hours}h`}</span>{' '}
              <span
                data-testid="labor-event-status"
                className={
                  ev.status === 'APPROVED'
                    ? 'rounded-full bg-emerald-500/20 px-2 py-0.5 text-xs font-medium text-emerald-300'
                    : ev.status === 'REJECTED'
                      ? 'rounded-full bg-red-500/20 px-2 py-0.5 text-xs font-medium text-red-300'
                      : 'rounded-full bg-amber-500/20 px-2 py-0.5 text-xs font-medium text-amber-300'
                }
              >
                {ev.status}
              </span>{' '}
              <span className="truncate">{ev.reason}</span>
              {canApprove && ev.status === 'PENDING' && (
                <>
                  <button data-testid="labor-approve-btn" onClick={() => void decide(ev.id, true)} disabled={busy}>
                    Aprobar
                  </button>
                  <button data-testid="labor-reject-btn" onClick={() => void decide(ev.id, false)} disabled={busy}>
                    Rechazar
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      </section>

      {notice && <div data-testid="labor-notice" role="status">{notice}</div>}
      {error && <div data-testid="labor-error" role="alert">{error}</div>}
    </div>
  );
}
